import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Crown, Quote, MapPin, Award, ArrowRight, Sparkles } from "lucide-react";

const TIER_META = {
  "lider-comunitate": {
    label: "Lider de comunitate",
    color: "from-[#F2C053] to-[#D4A63D]",
    text: "text-[#0A1020]",
    icon: Crown,
  },
  "ambasador": {
    label: "Ambasador",
    color: "from-[#E89BB8] to-[#D4748E]",
    text: "text-[#0A1020]",
    icon: Award,
  },
};

const initialsFor = (name) =>
  (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("") || "?";

export const WallOfChampions = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/champions", { params: { limit: 12 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ champions: [] }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  const champs = data?.champions || [];

  return (
    <section
      data-testid="wall-of-champions"
      className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mb-10"
    >
      <div className="dense-card relative overflow-hidden p-6 md:p-8">
        <div
          className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-15 pointer-events-none blur-3xl"
          style={{ background: "radial-gradient(circle, #E89BB8 0%, transparent 70%)" }}
        />

        <header className="relative mb-6 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.25em] uppercase text-[#D4A63D] bg-[#D4A63D]/10 border border-[#D4A63D]/30 px-2.5 py-1 rounded-sm">
              <Crown className="w-3 h-3" /> Wall of Champions
            </div>
            <h2 className="font-display text-2xl md:text-3xl text-[#F8F8F4] leading-tight mt-3">
              Oamenii care <span className="rose-grad-text">schimbă România</span>
            </h2>
            <p className="mt-2 text-[13px] text-[#A0AABF] max-w-2xl leading-relaxed">
              Lideri și ambasadori ai mișcării TatiSiSofia.ro. Fiecare petiție semnată, fiecare poveste împărtășită, fiecare comentariu util — îi aduce mai aproape de schimbare.
            </p>
          </div>
          <Link
            to="/profil"
            data-testid="champions-cta-profile"
            className="text-[11px] font-bold tracking-widest text-[#E89BB8] hover:text-[#F2BBD0] inline-flex items-center gap-1.5"
          >
            DEVINO CAMPION <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </header>

        {champs.length === 0 ? (
          <div className="relative cinematic-card rounded-lg p-8 text-center">
            <Sparkles className="w-8 h-8 text-[#D4A63D] mx-auto mb-3" />
            <h3 className="font-display text-lg text-[#F8F8F4]">Locul tău e liber!</h3>
            <p className="text-[13px] text-[#A0AABF] mt-2 max-w-md mx-auto">
              Nimeni nu a atins încă rangul de Ambasador (400+ puncte). Fii primul nume pe Wall of Champions — semnează petiții, împărtășește povești, ajută alți părinți.
            </p>
            <Link
              to="/autentificare"
              data-testid="champions-empty-cta"
              className="mt-4 inline-flex btn-gold rounded-sm px-5 py-2.5 text-[11px] font-bold tracking-widest items-center gap-2"
            >
              ÎNCEPE ACUM <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ) : (
          <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
            {champs.map((c, i) => {
              const tier = TIER_META[c.tier] || TIER_META["ambasador"];
              const TierIcon = tier.icon;
              return (
                <article
                  key={c.user_id || i}
                  data-testid={`champion-${i}`}
                  className="relative cinematic-card rounded-lg p-4 group hover:border-[#D4A63D]/40 transition-colors"
                >
                  {/* Tier ribbon */}
                  <div className={`absolute -top-2 left-3 inline-flex items-center gap-1 text-[9px] font-bold tracking-[0.2em] uppercase px-2 py-1 rounded-sm bg-gradient-to-r ${tier.color} ${tier.text}`}>
                    <TierIcon className="w-2.5 h-2.5" /> {tier.label}
                  </div>

                  <div className="mt-2 flex items-start gap-3">
                    {c.avatar_url ? (
                      <img
                        src={c.avatar_url}
                        alt={c.name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-[#D4A63D]/30 shrink-0"
                      />
                    ) : (
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${tier.color} grid place-items-center font-display text-base ${tier.text} shrink-0 border-2 border-white/15`}>
                        {initialsFor(c.name)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-display text-[15px] text-[#F8F8F4] leading-tight truncate">{c.name}</div>
                      <div className="text-[10.5px] text-[#A0AABF] mt-0.5 flex items-center gap-2">
                        {c.county && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {c.county}
                          </span>
                        )}
                        <span className="text-[#D4A63D] font-mono">
                          {(c.points || 0).toLocaleString("ro-RO")} pct
                        </span>
                      </div>
                    </div>
                  </div>

                  {c.quote ? (
                    <blockquote className="mt-3 text-[12.5px] text-[#F8F8F4]/85 italic leading-relaxed pl-3 border-l-2 border-[#E89BB8]/40 line-clamp-3">
                      <Quote className="w-3 h-3 text-[#E89BB8]/70 inline mr-1 -mt-1" />
                      {c.quote}
                    </blockquote>
                  ) : (
                    <div className="mt-3 text-[11.5px] text-[#6C7A9C] italic">Fără citat public</div>
                  )}

                  {c.badges && c.badges.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap gap-1.5">
                      {c.badges.slice(0, 4).map((b) => (
                        <span key={b} className="text-[9px] uppercase tracking-wider text-[#D4A63D] bg-[#D4A63D]/10 border border-[#D4A63D]/25 px-1.5 py-0.5 rounded-sm">
                          {b.replace(/-/g, " ")}
                        </span>
                      ))}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default WallOfChampions;
